import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PermissionGate } from "@/components/common/PermissionGate";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Edit, MoreVertical, Trash2 } from "lucide-react";
import {
  formatCurrency,
  fromMinorUnits,
  type CurrencyCode,
} from "@/lib/currency";
import type { PricingTierListDto } from "@/types/pricing-tier";
import { useDeletePricingTier } from "@/hooks/usePricingTiers";

// ============================================================================
// Types
// ============================================================================

interface PricingTierTableProps {
  tiers: PricingTierListDto[];
  isLoading?: boolean;
  currency: string;
  onEdit?: (tier: PricingTierListDto) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format quantity range for display
 * Examples: "1-10 units", "11-100 units", "100+ units"
 */
function formatQuantityRange(
  minQuantity: number,
  maxQuantity?: number,
): string {
  if (maxQuantity === undefined || maxQuantity === null) {
    return `${minQuantity.toLocaleString()}+ units`;
  }
  return `${minQuantity.toLocaleString()}-${maxQuantity.toLocaleString()} units`;
}

/**
 * Get tier order badge variant
 */
function getTierOrderBadge(order: number): {
  text: string;
  variant: "default" | "secondary" | "outline";
} {
  if (order === 1) {
    return { text: "1st", variant: "default" };
  } else if (order === 2) {
    return { text: "2nd", variant: "secondary" };
  } else if (order === 3) {
    return { text: "3rd", variant: "outline" };
  } else {
    return { text: `${order}th`, variant: "outline" };
  }
}

// ============================================================================
// Component
// ============================================================================

export function PricingTierTable({
  tiers,
  isLoading = false,
  currency,
  onEdit,
}: PricingTierTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PricingTierListDto | null>(
    null,
  );
  const deleteMutation = useDeletePricingTier();

  const handleDeleteClick = (tier: PricingTierListDto) => {
    setSelectedTier(tier);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedTier) {
      await deleteMutation.mutateAsync(selectedTier.id);
      setDeleteDialogOpen(false);
      setSelectedTier(null);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  // Render empty state
  if (tiers.length === 0) {
    return (
      <div className="text-center py-12 border rounded-md bg-muted/10">
        <div className="flex flex-col items-center gap-2">
          <div className="p-3 bg-muted rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
              />
            </svg>
          </div>
          <p className="text-muted-foreground font-medium">
            No pricing tiers found
          </p>
          <p className="text-sm text-muted-foreground">
            Create volume-based pricing tiers to offer discounts at different
            quantity levels
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Tier Order</TableHead>
              <TableHead>Tier Name</TableHead>
              <TableHead>Quantity Range</TableHead>
              <TableHead>Price/Unit</TableHead>
              <TableHead>Flat Fee</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.map((tier) => {
              const orderBadge = getTierOrderBadge(tier.tierOrder);
              return (
                <TableRow key={tier.id}>
                  <TableCell>
                    <Badge
                      variant={orderBadge.variant}
                      className="font-semibold"
                    >
                      {orderBadge.text}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {tier.tierName ? (
                      <span className="font-medium">{tier.tierName}</span>
                    ) : (
                      <span className="text-muted-foreground italic">
                        No name
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatQuantityRange(tier.minQuantity, tier.maxQuantity)}
                  </TableCell>
                  <TableCell className="font-mono font-semibold text-ok-ink">
                    {formatCurrency(
                      fromMinorUnits(
                        tier.pricePerUnit,
                        currency as CurrencyCode,
                      ),
                      currency,
                    )}
                  </TableCell>
                  <TableCell className="font-mono">
                    {tier.flatFee ? (
                      <span className="text-info-ink">
                        {formatCurrency(
                          fromMinorUnits(
                            tier.flatFee,
                            currency as CurrencyCode,
                          ),
                          currency,
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                          {onEdit && (
                            <DropdownMenuItem onClick={() => onEdit(tier)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(tier)}
                            className="text-destructive-ink"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </PermissionGate>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Tier</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to delete this pricing tier
              {selectedTier?.tierName ? ` "${selectedTier.tierName}"` : ""}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
